import { useCallback, useContext, useEffect, useState } from 'react';
import {
  Controller,
  UseFormReturn,
  useFieldArray,
  useWatch,
} from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore framer-motion is in the parent dir
import { AnimatePresence } from 'framer-motion';

import { AddActionButton } from 'components/AddActionButton';
import { Label } from 'components/Label';
import { Select } from 'components/Select';
import { Input } from 'components/input/Input';
import { TemperatureUnitSelector } from 'components/input/TemperatureUnitSelector';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { CommodityItemCard } from 'pages/QuoteView/LoadBuilding/TurvoSectionForms/components/CommodityItemCard';
import {
  EQUIPMENT_PLACEHOLDER,
  EQUIPMENT_REQUIRED_MESSAGE,
  validateEquipmentRequired,
} from 'pages/QuoteView/LoadBuilding/utils/suggestedEquipment';
import { NormalizedLoad, Unit, createNewCommodity } from 'types/Load';
import { MaybeUndef } from 'types/UtilityTypes';
import { TemperatureUnit } from 'types/enums/TemperatureUnit';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import { validateRequiredSelectMembership } from 'utils/formValidation';
import { devDisableRequiredFields } from 'utils/loadInfoAndBuilding';

import { LoadBuildingTextInput } from '../components/LoadBuildingTextInput';
import {
  equipmentList,
  equipmentSizeList,
  serviceTypes,
  weightUnitsList,
} from './constants';
import {
  normalizeTurvoTransportSizeForForm,
  normalizeTurvoTransportTypeForForm,
} from './utils';

export function SpecificationsForm({
  formMethods,
  specsSelectMountKey,
  isCreateMode = true,
}: {
  formMethods: UseFormReturn<NormalizedLoad>;
  /** Incremented in TurvoLoadForm when the load form `reset()` runs; keeps Radix Select in sync without remounting on every selection. */
  specsSelectMountKey?: MaybeUndef<number>;
  isCreateMode?: boolean;
}) {
  const specsSelectRemountKey = specsSelectMountKey ?? 0;

  const {
    control,
    formState: { errors },
    setValue,
    watch,
    getValues,
  } = formMethods;

  const modes = [
    'TL',
    'LTL',
    'Drayage',
    'Ocean',
    'Air',
    'Rail',
    'Parcel',
    'Intermodal',
  ];

  const {
    currentState: { clickedSuggestion },
  } = useContext(SidebarStateContext);

  const [tempUnit, setTempUnit] = useState<TemperatureUnit>(
    TemperatureUnit.Fahrenheit
  );
  const [tempDisplayValue, setTempDisplayValue] = useState<string>('');
  const [isTempInputFocused, setIsTempInputFocused] = useState<boolean>(false);

  const formatDisplayTemp = useCallback(
    (fahrenheit: MaybeUndef<number>) => {
      if (fahrenheit === undefined || fahrenheit === null) return '';
      if (tempUnit === 'F') return fahrenheit.toString();
      return (((fahrenheit - 32) * 5) / 9).toFixed(1);
    },
    [tempUnit]
  );

  const parseInputToFahrenheit = (value: string) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return null;
    if (tempUnit === 'F') return numValue;
    return (numValue * 9) / 5 + 32;
  };

  // When the unit changes, we need to update the form value to reflect the new unit.
  useEffect(() => {
    const currentFahrenheit = getValues('specifications.minTempFahrenheit');
    setValue('specifications.minTempFahrenheit', currentFahrenheit, {
      shouldDirty: true,
    });
  }, [tempUnit, getValues, setValue]);

  const isCommoditiesSupported = true;
  const watchedTotalWeight = watch('specifications.totalWeight.val');
  const watchedWeightUnit = watch('specifications.totalWeight.unit');
  const watchedTransportType = useWatch({
    control,
    name: 'specifications.transportType',
  });
  const watchedTransportSize = useWatch({
    control,
    name: 'specifications.transportSize',
  });

  useEffect(() => {
    if (watchedTotalWeight > 0 && !watchedWeightUnit) {
      setValue('specifications.totalWeight.unit', Unit.Pound, {
        shouldDirty: true,
      });
    }
  }, [watchedTotalWeight, watchedWeightUnit, setValue]);

  // Keep form state aligned with canonical Select values (same mapping as display),
  // so submit payloads never retain legacy/API strings the UI masks over.
  useEffect(() => {
    const normalized = normalizeTurvoTransportTypeForForm(watchedTransportType);
    const current = watchedTransportType ?? '';
    if (normalized !== current) {
      setValue('specifications.transportType', normalized, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [watchedTransportType, setValue]);

  useEffect(() => {
    const normalized = normalizeTurvoTransportSizeForForm(watchedTransportSize);
    const current = watchedTransportSize ?? '';
    if (normalized !== current) {
      setValue('specifications.transportSize', normalized, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [watchedTransportSize, setValue]);

  const isRefrigerated =
    watchedTransportType?.toLowerCase().includes('reefer') ||
    watchedTransportType?.toLowerCase().includes('refrigerated');

  // Watch for the min temp to be set
  const watchedMinTemp = watch('specifications.minTempFahrenheit');

  useEffect(() => {
    if (!isTempInputFocused) {
      setTempDisplayValue(formatDisplayTemp(watchedMinTemp));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedMinTemp, isTempInputFocused, formatDisplayTemp]);

  // Clear temps only when equipment no longer requires them. Depending on the
  // raw `watchedTransportType` string would re-run this after normalization
  // `setValue` (e.g. "REEFER" → "Reefer") and wipe temps while still refrigerated.
  useEffect(() => {
    if (
      clickedSuggestion &&
      clickedSuggestion.pipeline === SuggestionPipelines.LoadBuilding
    ) {
      return;
    }

    if (isRefrigerated) {
      return;
    }

    setValue('specifications.minTempFahrenheit', null, { shouldDirty: true });
    setValue('specifications.maxTempFahrenheit', null, { shouldDirty: true });
  }, [setValue, isRefrigerated, clickedSuggestion]);

  // When minTempFahrenheit changes, update maxTempFahrenheit to match
  useEffect(() => {
    if (watchedMinTemp !== undefined) {
      setValue('specifications.maxTempFahrenheit', watchedMinTemp);
    }
  }, [watchedMinTemp, setValue]);

  return (
    <Grid cols='4' gap='sm' className='mx-0 w-full'>
      {/* Only Truckloads supported rn, not LTL */}
      <div className='col-span-4'>
        <Label hideAIHint={true} name={'mode'} required={true}>
          Mode
        </Label>
        {/* <div className='flex flex-row w-full items-center gap-2'> */}
        <Controller
          name='mode'
          control={control}
          rules={{ required: devDisableRequiredFields ? false : 'Required' }}
          render={({ field }) => (
            <div className='text-neutral-500'>
              <Select
                showSearch
                name={field.name}
                disabled={false} // Only Truckloads supported rn, not LTL
                className='h-8'
                placeholder={'Choose'}
                optionFilterProp='children'
                onChange={field.onChange}
                value={field.value}
                options={modes?.map((mode) => ({
                  value: mode,
                  label: mode,
                }))}
              />
            </div>
          )}
        />
        {/* </div> */}
        <ErrorMessage
          errors={errors}
          name={'mode'}
          render={({ message }: { message: string }) => (
            <Typography variant='body-xs' className='text-error-500'>
              {message}
            </Typography>
          )}
        />
      </div>
      <div className='col-span-4'>
        <Label
          name={'serviceType'}
          // We always default this value so don't show AI hint unless there's a suggestion
          hideAIHint={
            !clickedSuggestion ||
            clickedSuggestion.pipeline !== SuggestionPipelines.LoadBuilding
          }
          required={true}
        >
          Service Type
        </Label>
        <Controller
          name='specifications.serviceType'
          control={control}
          rules={{ required: devDisableRequiredFields ? false : 'Required' }}
          render={({ field }) => (
            <div className='text-neutral-500'>
              <Select
                showSearch
                name={field.name}
                disabled={false}
                className='h-8'
                placeholder={'Choose'}
                optionFilterProp='children'
                onChange={field.onChange}
                value={field.value}
                options={serviceTypes?.map((serviceType) => ({
                  value: serviceType,
                  label: serviceType,
                }))}
              />
            </div>
          )}
        />
        <ErrorMessage
          errors={errors}
          name={'specifications.serviceType'}
          render={({ message }: { message: string }) => (
            <Typography variant='body-xs' className='text-error-500'>
              {message}
            </Typography>
          )}
        />
      </div>
      <div className='col-span-2'>
        <Label
          name={'specifications.transportType'}
          // We always default this value so don't show AI hint unless there's a suggestion
          hideAIHint={
            !clickedSuggestion ||
            clickedSuggestion.pipeline !== SuggestionPipelines.LoadBuilding
          }
          required={true}
        >
          Equipment
        </Label>
        <Controller
          name='specifications.transportType'
          control={control}
          rules={{
            validate: (value) => {
              if (!isCreateMode) {
                return validateEquipmentRequired(value);
              }
              return validateRequiredSelectMembership(
                normalizeTurvoTransportTypeForForm(value),
                equipmentList ?? [],
                { message: EQUIPMENT_REQUIRED_MESSAGE }
              );
            },
          }}
          render={({ field }) => {
            const normalized = normalizeTurvoTransportTypeForForm(field.value);
            const selected = String(field.value ?? '').trim();
            let selectValue: string | undefined;
            if (selected) {
              if (!isCreateMode) {
                selectValue = normalized || selected;
              } else if (normalized) {
                selectValue = normalized;
              }
            }
            return (
              <Select
                key={`turvo-equip-${specsSelectRemountKey}`}
                showSearch
                name={field.name}
                className='h-8'
                placeholder={EQUIPMENT_PLACEHOLDER}
                optionFilterProp='children'
                onChange={(selectedOption) =>
                  field.onChange(
                    normalizeTurvoTransportTypeForForm(
                      selectedOption == null ? '' : String(selectedOption)
                    )
                  )
                }
                value={selectValue}
                options={equipmentList?.map((type) => ({
                  value: type,
                  label: type,
                }))}
              />
            );
          }}
        />
        <ErrorMessage
          errors={errors}
          name={'specifications.transportType'}
          render={({ message }: { message: string }) => (
            <Typography variant='body-xs' className='text-error-500'>
              {message}
            </Typography>
          )}
        />
      </div>
      <div className='col-span-2'>
        <Label name={'specifications.transportSize'} required={false}>
          Size
        </Label>
        <Controller
          name='specifications.transportSize'
          control={control}
          rules={{ required: false }}
          render={({ field }) => (
            <Select
              key={`turvo-size-${specsSelectRemountKey}`}
              showSearch
              name={field.name}
              className='h-8'
              placeholder={'Choose'}
              optionFilterProp='children'
              onChange={(selected) =>
                field.onChange(
                  normalizeTurvoTransportSizeForForm(
                    selected == null ? '' : String(selected)
                  )
                )
              }
              value={normalizeTurvoTransportSizeForForm(field.value) || ''}
              options={equipmentSizeList?.map((size) => ({
                value: size,
                label: size,
              }))}
            />
          )}
        />
      </div>

      <Grid cols='2' gap='sm' className='mx-0 w-full col-span-4'>
        <div className='col-span-1'>
          <LoadBuildingTextInput
            name='specifications.totalWeight.val'
            label={`Equipment Weight`}
            inputType='number'
            options={{ valueAsNumber: true, min: 0 }}
            required={false}
            labelClassName='whitespace-nowrap'
          />
        </div>

        <Flex direction='col' className='col-span-1'>
          <Label
            name={'specifications.totalWeight.unit'}
            required={watchedTotalWeight > 0}
          >
            Units
          </Label>
          <Controller
            name='specifications.totalWeight.unit'
            control={control}
            rules={{ required: watchedTotalWeight > 0 }}
            render={({ field }) => (
              <Select
                showSearch
                name={field.name}
                className='h-8'
                placeholder={'Choose'}
                optionFilterProp='children'
                onChange={field.onChange}
                value={field.value ?? undefined}
                options={weightUnitsList?.map((unit) => ({
                  value: unit,
                  label: unit,
                }))}
              />
            )}
          />
        </Flex>
      </Grid>

      <div className='col-span-4'>
        <LoadBuildingTextInput
          name='specifications.commodities'
          label='Description'
          required={false}
        />
      </div>

      {isRefrigerated && (
        <>
          <div className='col-span-4'>
            <Label name='temperature' required={isRefrigerated}>
              Temperature
            </Label>
            <div className='relative flex items-center'>
              <Controller
                name='specifications.minTempFahrenheit'
                control={control}
                rules={{
                  required: isRefrigerated
                    ? 'Temperature is required for refrigerated equipment'
                    : false,
                }}
                render={({ field }) => (
                  <div className='flex flex-row w-full items-center gap-2'>
                    <Input
                      {...field}
                      disabled={!isRefrigerated}
                      placeholder={isRefrigerated ? 'e.g. 32' : 'N/A'}
                      value={tempDisplayValue}
                      onChange={(e) => setTempDisplayValue(e.target.value)}
                      onFocus={() => setIsTempInputFocused(true)}
                      onBlur={() => {
                        setIsTempInputFocused(false);
                        const fahrenheit =
                          parseInputToFahrenheit(tempDisplayValue);
                        field.onChange(fahrenheit);
                      }}
                    />
                    <TemperatureUnitSelector
                      value={tempUnit}
                      onChange={(newUnit) => setTempUnit(newUnit)}
                    />
                  </div>
                )}
              />
            </div>
            <ErrorMessage
              errors={errors}
              name={'specifications.minTempFahrenheit'}
              render={({ message }: { message: string }) => (
                <p className='text-error-500 text-xs'>{message}</p>
              )}
            />
          </div>
        </>
      )}

      {isCommoditiesSupported && (
        <CommoditiesFieldArray formMethods={formMethods} />
      )}
    </Grid>
  );
}

function CommoditiesFieldArray({
  formMethods,
}: {
  formMethods: UseFormReturn<NormalizedLoad>;
}) {
  const { control } = formMethods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'commodities',
  });

  const addCommodity = () => append(createNewCommodity());

  return (
    <div className='col-span-4'>
      <AddActionButton
        onClick={addCommodity}
        buttonText='Add Item'
        descriptionText='Add item(s) for the shipment'
        buttonNamePosthog='add_commodity_item'
        className='mb-2'
      />

      <AnimatePresence>
        <div className={fields.length > 0 ? 'space-y-2' : ''}>
          {fields.map((field, index) => (
            <CommodityItemCard
              key={field.id}
              index={index}
              formMethods={formMethods}
              onRemove={remove}
            />
          ))}
        </div>
      </AnimatePresence>
    </div>
  );
}
